import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../contexts/AuthContext";
import useApiHelper from "../hooks/useApiHelper";
import { IVehicleLookupConfig, IVehicleLookupConfigResponse } from "../models/Vehicle";
import { DEFAULT_LOOKUP_CONFIG, parseLookupConfig } from "../utils/vehicleIdentifier";

const CACHE_KEY = "vehicleLookupConfig";
const CACHE_EXP_KEY = "vehicleLookupConfigExp";
const staleTime = 1000 * 60 * 60; // 1 hour: the mode is a property of the deployment, not the user

type CachedLookupConfig = IVehicleLookupConfig & { fromFallback?: boolean };

// Same localStorage mirror as the customer details, so a VIN-mode yard never paints its first
// screen in registration mode while the config loads.
const readCached = (): IVehicleLookupConfig | undefined => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const expiry = localStorage.getItem(CACHE_EXP_KEY);

    if (cached && cached !== "undefined" && expiry && Date.now() < parseInt(expiry)) {
      return parseLookupConfig(JSON.parse(cached));
    }
  } catch {
    // Storage can be unavailable (private windows); the API answer still arrives.
  }

  return undefined;
};

const writeCached = (config: IVehicleLookupConfig) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(config));
    localStorage.setItem(CACHE_EXP_KEY, (Date.now() + staleTime).toString());
  } catch {
    // Best effort.
  }
};

export default function useVehicleLookupConfig() {
  const { get } = useApiHelper();
  const { loggedIn } = useAuth();

  const config = useQuery<CachedLookupConfig>({
    queryKey: ["vehicleLookupConfig"],
    queryFn: async () => {
      try {
        const response = await get<IVehicleLookupConfigResponse>("/vehicle/lookup/config");
        const parsed = parseLookupConfig(response?.data);
        writeCached(parsed);
        return parsed;
      } catch {
        // Registration mode is the safe default; `fromFallback` retries it soon rather than pinning
        // a VIN-mode yard to the wrong mode after one failed boot.
        return { ...DEFAULT_LOOKUP_CONFIG, fromFallback: true };
      }
    },
    enabled: loggedIn,
    staleTime,
    cacheTime: staleTime,
    refetchOnWindowFocus: false,
    placeholderData: readCached,
    refetchInterval: (data) => (data?.fromFallback ? 30000 : false),
  });

  return {
    config,
    lookupConfig: (config.data as IVehicleLookupConfig | undefined) ?? DEFAULT_LOOKUP_CONFIG,
  };
}
